import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ProjectContainer,
  ProjectTitle,
  Tagline,
  MetricsStrip,
  StatCard,
  ProjectDescription,
  ProjectInfo,
  SectionTitle,
  FlowDiagram,
  FlowBox,
  FlowArrow,
  Footnote,
  Chips,
  ProjectWrapper,
  CheckList,
  Caption,
  Divider,
} from './styles';

interface ProjectDagProps {
}

const ProjectDag: React.FC<ProjectDagProps> = () => {
  const { t } = useLanguage();

  return (
    <ProjectContainer>
      <ProjectTitle>
        <h2>{t('dag.title')}</h2>
        <p className="tags">{t('dag.role')}</p>
      </ProjectTitle>

      <Tagline>{t('dag.tagline')}</Tagline>

      <MetricsStrip>
        <StatCard>
          <span className="value">{t('dag.statFullstack')}</span>
          <span className="label">{t('dag.statFullstackLabel')}</span>
        </StatCard>
        <StatCard>
          <span className="value">{t('dag.statLight')}</span>
          <span className="label">{t('dag.statLightLabel')}</span>
        </StatCard>
        <StatCard>
          <span className="value">{t('dag.statExtensible')}</span>
          <span className="label">{t('dag.statExtensibleLabel')}</span>
        </StatCard>
        <StatCard>
          <span className="value">{t('dag.statResilient')}</span>
          <span className="label">{t('dag.statResilientLabel')}</span>
        </StatCard>
      </MetricsStrip>

      <ProjectDescription>{t('dag.overview')}</ProjectDescription>

      <ProjectInfo>
        <p><b>{t('dag.roleLabel')}:</b> {t('dag.myRole')}</p>
        <p><b>{t('dag.stackLabel')}:</b> {t('dag.stack')}</p>
        <p><b>{t('dag.scaleLabel')}:</b> {t('dag.scale')}</p>
        <p>
          <b>{t('dag.githubLabel')}:</b>{' '}
          <a href="https://github.com/YuanyuanMaggie/dag_worflow_engine" target="_blank" rel="noopener noreferrer">
            github.com/YuanyuanMaggie/dag_worflow_engine
          </a>
        </p>
      </ProjectInfo>

      <ProjectWrapper>
        <img loading="lazy" decoding="async" src="/images/projects/dag-editor.webp" alt={t('dag.editorCaption')} />
        <Caption>{t('dag.editorCaption')}</Caption>
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t('dag.archTitle')}</SectionTitle>
        <FlowDiagram>
          <FlowBox>
            <div className="name">React Flow UI</div>
            <div className="sub">{t('dag.archFlowUi')}</div>
          </FlowBox>
          <FlowArrow>→</FlowArrow>
          <FlowBox>
            <div className="name">FastAPI</div>
            <div className="sub">{t('dag.archFlowApi')}</div>
          </FlowBox>
          <FlowArrow>→</FlowArrow>
          <FlowBox>
            <div className="name">PostgreSQL</div>
            <div className="sub">{t('dag.archFlowDb')}</div>
          </FlowBox>
        </FlowDiagram>
        <Footnote>{t('dag.archNodeIntro')}</Footnote>
        <Chips>
          <span>start</span>
          <span>end</span>
          <span>third_party · HTTP</span>
          <span>branch · conditional</span>
        </Chips>
        <Footnote>{t('dag.archTwoPhase')}</Footnote>
        <img loading="lazy" decoding="async" src="/images/projects/dag-workflow-detail.webp" alt={t('dag.workflowCaption')} />
        <Caption>{t('dag.workflowCaption')}</Caption>
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t('dag.deepDiveTitle')}</SectionTitle>
        <ul>
          <li>{t('dag.deepValidation')}</li>
          <li>{t('dag.deepScheduler')}</li>
          <li>{t('dag.deepBranching')}</li>
          <li>{t('dag.deepRetry')}</li>
          <li>{t('dag.deepStep')}</li>
          <li>{t('dag.deepHttp')}</li>
        </ul>
        <img loading="lazy" decoding="async" src="/images/projects/dag-run-detail.webp" alt={t('dag.runCaption')} />
        <Caption>{t('dag.runCaption')}</Caption>
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t('dag.tradeoffsTitle')}</SectionTitle>
        <ul>
          <li>{t('dag.tradeoffExecutor')}</li>
          <li>{t('dag.tradeoffPolling')}</li>
          <li>{t('dag.tradeoffTemplates')}</li>
          <li>{t('dag.tradeoffScheduler')}</li>
        </ul>
      </ProjectWrapper>

      <Divider />

      <ProjectWrapper>
        <SectionTitle>{t('dag.testingTitle')}</SectionTitle>
        <Footnote>{t('dag.testingDesc')}</Footnote>
        <CheckList>
          <li>{t('dag.testSequential')}</li>
          <li>{t('dag.testParallel')}</li>
          <li>{t('dag.testFailure')}</li>
          <li>{t('dag.testRetry')}</li>
          <li>{t('dag.testInactive')}</li>
        </CheckList>
      </ProjectWrapper>

      <Divider />

      <ProjectInfo>
        <p><b>{t('dag.dateLabel')}:</b> {t('dag.date')}</p>
      </ProjectInfo>
    </ProjectContainer>
  );
};

export default ProjectDag;
